import React, { useEffect, useRef } from 'react';

import { PlayButton } from '../components/PlayButton';
import { VideoPlayerCoreProps, GenericVideoPlayerInterface, trackVideoEngagement } from '../videoUtils';
import { loadCloudflareAPI, CloudflareStreamPlayer } from './cloudflareUtils';

function createCloudflarePlayerWrapper(player: CloudflareStreamPlayer): GenericVideoPlayerInterface {
    return {
        getCurrentTime: () => {
            return typeof player?.currentTime === 'number' ? player.currentTime : undefined;
        },
        getDuration: () => {
            return typeof player?.duration === 'number' ? player.duration : undefined;
        },
        isEnded: () => {
            return typeof player?.ended === 'boolean' ? player.ended === true : undefined;
        },
    };
}

export function CloudflarePlayerCore({
    videoUrl,
    videoId,
    className,
    hasControls,
    shouldAutoplay,
    playerRef,
    onPlay,
    onPause,
    onEnded,
    onReady,
}: VideoPlayerCoreProps) {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const [isCloudflareAPIReady, setIsCloudflareAPIReady] = React.useState(false);
    const [isPlaying, setIsPlaying] = React.useState(false);
    const nativePlayerRef = useRef<CloudflareStreamPlayer | null>(null);

    useEffect(() => {
        loadCloudflareAPI(() => setIsCloudflareAPIReady(true));
    }, []);

    const handleIframeClick = () => {
        const player = nativePlayerRef.current;
        if (!player) return;
        if (hasControls) return; // Only handle clicks when controls are disabled

        try {
            if (isPlaying) {
                player.pause();
            } else {
                player.play();
            }
        } catch (error) {
            console.log('Warning: Error toggling play/pause', error);
        }
    };

    useEffect(() => {
        if (!isCloudflareAPIReady) return;
        if (!videoUrl) return;
        if (!videoUrl.includes('cloudflarestream.com')) return;
        if (!videoId) {
            console.warn('Invalid Cloudflare Stream video URL');
            return;
        }

        if (iframeRef.current && window.Stream) {
            const player = window.Stream(iframeRef.current);
            nativePlayerRef.current = player;
            playerRef.current = createCloudflarePlayerWrapper(player);

            player.addEventListener('play', () => {
                trackVideoEngagement('play', videoUrl, [], [player.currentTime, player.duration]);
                setIsPlaying(true);
                onPlay();
            });
            player.addEventListener('pause', () => {
                trackVideoEngagement('pause', videoUrl, [], [player.currentTime, player.duration]);
                setIsPlaying(false);
                onPause();
            });
            player.addEventListener('ended', () => {
                trackVideoEngagement('ended', videoUrl, [], [player.duration]);
                setIsPlaying(false);
                onEnded();
            });
            player.addEventListener('seeking', () => {
                trackVideoEngagement('seeking', videoUrl, [], [player.currentTime, player.duration]);
            });
            player.addEventListener('seeked', () => {
                trackVideoEngagement('seeked', videoUrl, [], [player.currentTime, player.duration]);
            });
            player.addEventListener('volumechange', () => {
                trackVideoEngagement('volume_change', videoUrl, [], [player.volume]);
            });
            player.addEventListener('loadedmetadata', () => {
                trackVideoEngagement('loaded_metadata', videoUrl, [], [player.duration]);
                onReady();
            });
            player.addEventListener('error', () => {
                trackVideoEngagement('error', videoUrl, [], []);
            });

            player.controls = hasControls;
        }

        return () => {
            nativePlayerRef.current = null;
            playerRef.current = null;
        };
    }, [isCloudflareAPIReady, videoUrl, hasControls, videoId]);

    if (!videoUrl?.includes('cloudflarestream.com')) {
        console.warn('Invalid Cloudflare Stream video URL');
        return null;
    }

    if (!videoId) {
        console.warn('Invalid Cloudflare Stream video URL');
        return null;
    }

    let iframeSrc = videoUrl;
    if (shouldAutoplay) {
        const url = new URL(videoUrl);
        url.searchParams.set('autoplay', 'true');
        iframeSrc = url.toString();
    }

    const currentTime = nativePlayerRef.current?.currentTime ?? 0;
    const showPlayButton = !hasControls && !isPlaying && currentTime === 0;

    return (
        <div
            onClick={handleIframeClick}
            style={{
                width: '100%',
                height: '100%',
                position: 'relative',
                cursor: !hasControls ? 'pointer' : 'default',
            }}
        >
            {showPlayButton && <PlayButton />}
            <iframe
                ref={iframeRef}
                src={iframeSrc}
                className={className}
                style={{
                    border: 'none',
                    width: '100%',
                    height: '100%',
                    pointerEvents: hasControls ? 'auto' : 'none',
                }}
                allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                allowFullScreen
                title="Video player"
            />
        </div>
    );
}
